/**
 * Shared credit-balance formatting helpers.
 *
 * Two renderings are used across the credit UI:
 *   - compact (`formatCreditsCompact`) — the space-constrained pill number.
 *     It NEVER rounds up (which would overstate the balance): the value is
 *     TRUNCATED, and a trailing "+" is appended whenever anything below the
 *     shown precision was dropped — either a fractional part or digits hidden
 *     by the k/M/B compaction. e.g. 799999.95 → "799.9k+", 800000 → "800k",
 *     1000000 → "1M", 42.5 → "42+".
 *   - exact (`formatCreditsExact`) — full-precision, locale-grouped detail for
 *     hover popovers and reserved chips, e.g. 799999.95185 → "799,999.95185".
 */

/** Tolerance to absorb floating-point noise when flooring / comparing. */
const EPSILON = 1e-9;

const UNITS: ReadonlyArray<{ limit: number; suffix: string }> = [
  { limit: 1_000_000_000, suffix: "B" },
  { limit: 1_000_000, suffix: "M" },
  { limit: 1_000, suffix: "k" },
];

function trimTrailingZero(value: string): string {
  return value.endsWith(".0") ? value.slice(0, -2) : value;
}

/**
 * Compact, suffixed pill rendering that never overstates the balance.
 * Truncates (does not round up) and appends "+" when credits are hidden by the
 * truncation or compaction. e.g. 5234 → "5.2k+", 800000 → "800k".
 */
export function formatCreditsCompact(value: number): string {
  if (!Number.isFinite(value) || value <= 0) {
    return "0";
  }

  // Sub-1000: plain integer, truncated (never rounded up).
  if (value < 1000) {
    const whole = Math.floor(value + EPSILON);
    const plus = value - whole > EPSILON ? "+" : "";
    return `${whole}${plus}`;
  }

  for (const { limit, suffix } of UNITS) {
    if (value >= limit) {
      // Floor the quotient to one decimal place (never rounds up).
      const tenths = Math.floor((value / limit) * 10 + EPSILON);
      const rendered = trimTrailingZero((tenths / 10).toFixed(1));
      // Exact integer arithmetic for the value the rendered figure represents,
      // so "+" reflects any credits hidden by flooring / compaction.
      const represented = tenths * (limit / 10);
      const plus = value - represented > EPSILON ? "+" : "";
      return `${rendered}${suffix}${plus}`;
    }
  }

  // Unreachable given the guards above, but keeps the return type total.
  return String(Math.floor(value + EPSILON));
}

/** Full-precision, locale-grouped rendering for hover detail / reserved chip. */
export function formatCreditsExact(n: number): string {
  return n.toLocaleString(undefined, { maximumFractionDigits: 5 });
}

/**
 * Format a decimal credit STRING (e.g. "100000") with locale thousands
 * separators, e.g. "100,000". Used for the fixed credit ALLOWANCES the backend
 * returns as decimal strings (top-up packages, plan monthly allowances) — not
 * the live float balances above.
 *
 * Returns null when the value is absent, zero, or unparseable, so callers can
 * render a "no included credits" affordance in that case.
 */
export function formatCreditString(
  credits: string | null | undefined,
  locale?: string,
): string | null {
  if (credits == null) return null;
  const parsed = Number(credits);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return parsed.toLocaleString(locale);
}
